import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useTonAddress } from '@tonconnect/ui-react';
import { useCallback } from 'react';
import { api } from '../lib/api';
import type { InAppNotification } from '../lib/api';

const NOTIF_KEY = 'notifications';

export function useNotifications() {
  const address = useTonAddress();
  const qc = useQueryClient();

  const { data } = useQuery({
    queryKey: [NOTIF_KEY, address],
    queryFn: () => api.getNotifications(address),
    enabled: !!address,
    refetchInterval: 30_000,
    staleTime: 10_000,
  });

  const markRead = useCallback(
    async (ids: number[]) => {
      if (!address) return;
      await api.markNotificationsRead(address, ids);
      qc.invalidateQueries({ queryKey: [NOTIF_KEY, address] });
    },
    [address, qc],
  );

  const markAllRead = useCallback(async () => {
    if (!address) return;
    await api.markNotificationsRead(address);
    qc.invalidateQueries({ queryKey: [NOTIF_KEY, address] });
  }, [address, qc]);

  const clearAll = useCallback(async () => {
    if (!address) return;
    await api.clearNotifications(address);
    qc.invalidateQueries({ queryKey: [NOTIF_KEY, address] });
  }, [address, qc]);

  return {
    notifications: (data?.notifications ?? []) as InAppNotification[],
    unreadCount: data?.unreadCount ?? 0,
    markRead,
    markAllRead,
    clearAll,
  };
}
